// Avondplanning (spec AV1): morgen klaarzetten in vijf korte stappen, elk met één knop "Verder".
// "Snel plannen" (AV1 AC6) is stap 2 en 3 voor vandaag, de ochtend na een overgeslagen avond.
import { useEffect, useRef, useState } from "react";
import { availabilityLine, useHousehold, useOthersTime, useShowPartner } from "../lib/samen";
import { Sparkles } from "lucide-react";
import { aiError, aiPlan, useAiEnabled, type PlanSuggestion } from "../lib/ai";
import { MealLine } from "../components/MealSheet";
import { GymChoice } from "./GymChoice";
import { useLiveQuery } from "dexie-react-hooks";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { gymOffer } from "@shared/evening";
import { occasionalList, isDue } from "@shared/routines";
import { sortBlocks } from "@shared/rhythm";
import { addDays, displayDate, displayTime, planningDate, timeToMinutes } from "@shared/time";
import { openDayType } from "../components/DayTypeSheet";
import { openTask } from "../components/TaskSheet";
import { Button, cn } from "../components/ui";
import { useOccurrences } from "../lib/appointments";
import { db, type PlanEntry, type Routine } from "../lib/db";
import { finishSession, planRoutineOn, saveStep, setChecked, useChecklist, useSession } from "../lib/evening";
import { navigate } from "../lib/hooks";
import { isPausedIn, useRhythm } from "../lib/rhythm";
import { useCompletions, useRoutines } from "../lib/routines";
import type { Me } from "../lib/session";
import { planTask, setTaskStatus, unplanEntries } from "../lib/tasks";

const EVENING_STEPS = [1, 2, 3, 4, 5];
const QUICK_STEPS = [2, 3, 5];

const STEP_TITLES: Record<number, string> = {
  1: "Vandaag afsluiten",
  2: "Morgen in één oogopslag",
  3: "Inplannen",
  4: "Klaarleggen",
  5: "Klaar",
};

export function EveningScreen({ me, quick }: { me: Me; quick: boolean }) {
  const dayStart = timeToMinutes(me.settings?.day_starts_at ?? "04:00");
  const today = planningDate(new Date(), dayStart);
  // In the evening the flow plans tomorrow, also after midnight until 04:00 (AV1 AC5); quick plans today.
  const forDate = quick ? today : addDays(today, 1);
  const mode = quick ? "quick" : "evening";
  const steps = quick ? QUICK_STEPS : EVENING_STEPS;
  const session = useSession(forDate, mode);
  const [chosen, setStep] = useState<number | null>(null);
  const starting = useRef(false);
  // Opening again carries on at the step where it stopped (AV1 AC3).
  const step = chosen ?? (session ? (session.finished_at ? 5 : session.step) : null);

  useEffect(() => {
    if (session !== null || starting.current) return;
    starting.current = true;
    void saveStep(null, forDate, mode, steps[0]!);
  }, [session, forDate, mode, steps]);

  if (step === null || !session) return <main aria-busy className="min-h-dvh" />;

  const index = steps.indexOf(step);
  async function go(next: number) {
    if (next === 5 && !session!.finished_at) await finishSession(session!.id, session!.started_at, forDate, mode);
    else if (next !== 5) await saveStep(session!, forDate, mode, next);
    setStep(next);
    window.scrollTo({ top: 0 });
  }

  return (
    <main className="mx-auto max-w-xl px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-44">
      <header className="mb-5 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-ink-soft">
            {quick ? "Snel plannen voor vandaag" : `Morgen klaarzetten: ${displayDate(forDate)}`}
            {step !== 5 && `, stap ${index + 1} van ${steps.length}`}
          </p>
          <h1 className="text-[1.75rem] leading-tight font-bold">{quick && step === 2 ? "Vandaag in één oogopslag" : STEP_TITLES[step]}</h1>
        </div>
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
          }}
          aria-label={step === 5 ? "Sluiten" : "Later verder"}
          className="-mr-2 flex size-11 shrink-0 items-center justify-center rounded-xl text-ink-soft"
        >
          <X aria-hidden className="size-6" />
        </a>
      </header>

      {step === 1 && <CloseToday today={today} tomorrow={forDate} />}
      {step === 2 && <Overview date={forDate} />}
      {step === 3 && <PlanIn date={forDate} today={today} />}
      {step === 4 && <LayOut date={forDate} />}
      {step === 5 && <Done today={today} forDate={forDate} quick={quick} />}

      {step !== 5 && (
        <div className="mt-8 flex gap-2">
          {index > 0 && (
            <Button variant="secondary" onClick={() => void go(steps[index - 1]!)}>
              Terug
            </Button>
          )}
          <Button className="flex-1" onClick={() => void go(steps[index + 1]!)}>
            Verder
          </Button>
        </div>
      )}
    </main>
  );
}

function Empty({ children }: { children: string }) {
  return <p className="rounded-2xl border border-dashed border-mist p-4 text-ink-soft">{children}</p>;
}

/** Stap 1: open taken van vandaag naar morgen, zonder dag (Te doen) of weg. */
function CloseToday({ today, tomorrow }: { today: string; tomorrow: string }) {
  const open = useLiveQuery(
    () =>
      db.plan_entries
        .where("plan_date")
        .equals(today)
        .filter((e) => !e.deleted_at && e.kind === "task" && e.status === "planned")
        .toArray(),
    [today],
  );
  if (!open) return null;
  if (!open.length) return <Empty>Niets meer open van vandaag.</Empty>;

  async function act(entry: PlanEntry, action: "tomorrow" | "list" | "away") {
    if (!entry.task_id) return;
    if (action === "tomorrow") await planTask(entry.task_id, { date: tomorrow }, "nog_open_tomorrow");
    if (action === "list") await unplanEntries([entry], "back_to_list");
    if (action === "away") await setTaskStatus(entry.task_id, "cancelled");
    toast(action === "tomorrow" ? "Op morgen gezet" : action === "list" ? "Zonder dag, bij Te doen" : "Weggezet");
  }

  return (
    <ul className="flex flex-col gap-3">
      {open.map((e) => (
        <li key={e.id} className="rounded-2xl border border-mist bg-surface p-3">
          <p className="mb-2 [overflow-wrap:anywhere]">{e.title}</p>
          <div className="grid grid-cols-3 gap-1">
            {(
              [
                ["tomorrow", "Morgen"],
                ["list", "Zonder dag"],
                ["away", "Weg"],
              ] as const
            ).map(([action, label]) => (
              <button
                key={action}
                type="button"
                onClick={() => void act(e, action)}
                aria-label={`${e.title}: ${label}`}
                className="min-h-11 rounded-lg border-2 border-mist px-1 text-sm font-semibold"
              >
                {label}
              </button>
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Stap 2: morgen in één oogopslag. */
function Overview({ date }: { date: string }) {
  const occurrences = useOccurrences(date, date);
  const counted = useRoutines();
  const day = useLiveQuery(async () => {
    const plan = await db.day_plans
      .where("plan_date")
      .equals(date)
      .filter((p) => !p.deleted_at)
      .first();
    const entries = await db.plan_entries
      .where("plan_date")
      .equals(date)
      .filter((e) => !e.deleted_at && (e.status === "planned" || e.status === "done" || e.status === "skipped"))
      .toArray();
    const blocks = plan
      ? await db.day_blocks
          .where("day_plan_id")
          .equals(plan.id)
          .filter((b) => !b.deleted_at)
          .toArray()
      : [];
    return { plan, entries, blocks };
  }, [date]);
  if (!day || !counted) return null;
  const blockOf = (id: string | null) => day.blocks.find((b) => b.id === id);
  const gymIds = new Set(counted.filter((r) => r.weekly_target).map((r) => r.id));
  const skippedGym = day.entries.find((e) => e.kind === "routine" && e.status === "skipped" && gymIds.has(e.routine_id ?? ""));
  const routines = day.entries.filter((e) => e.kind === "routine" && e.status !== "skipped");
  const gym = routines.find((e) => gymIds.has(e.routine_id ?? ""));
  const gymTime = gym ? (gym.at_local ?? blockOf(gym.day_block_id)?.start_local ?? null) : null;
  const tasks = day.entries.filter((e) => e.kind === "task" && e.status !== "skipped");

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-2xl border border-mist bg-surface p-3">
        <h2 className="text-sm text-ink-soft">Dagtype</h2>
        <button
          type="button"
          onClick={() => openDayType(date)}
          aria-label={`Dagtype ${day.plan?.template_name ?? ""}, wijzigen`}
          className="flex min-h-11 w-full items-center justify-between gap-2 text-left text-lg font-semibold"
        >
          {day.plan?.template_name ?? "Nog niet klaar"}
          <span className="text-sm font-normal text-pine">Wijzigen</span>
        </button>
      </section>
      <section className="rounded-2xl border border-mist bg-surface p-3">
        <h2 className="text-sm text-ink-soft">Gym</h2>
        <GymChoice date={date} today={planningDate(new Date())} gym={gym} gymTime={gymTime} skipped={skippedGym} blocks={day.blocks} />
      </section>
      <PartnerTomorrow date={date} />
      <section className="rounded-2xl border border-mist bg-surface p-3">
        <h2 className="text-sm text-ink-soft">Wie eet mee</h2>
        <MealLine date={date} editable inBlock={false} />
      </section>
      <section className="rounded-2xl border border-mist bg-surface p-3">
        <h2 className="mb-1 text-sm text-ink-soft">Afspraken</h2>
        {occurrences?.length ? (
          <ul>
            {occurrences.map((o) => (
              <li key={o.recurrenceLocal + o.appointmentId} className="flex justify-between gap-3">
                <span className="[overflow-wrap:anywhere]">{o.title}</span>
                <span className="text-ink-soft tabular">{displayTime(o.startLocal.slice(11, 16))}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p>Geen afspraken</p>
        )}
      </section>
      <section className="rounded-2xl border border-mist bg-surface p-3">
        <h2 className="mb-1 text-sm text-ink-soft">Staat er al</h2>
        {routines.length + tasks.length ? (
          <ul className="list-disc pl-5">
            {[...routines, ...tasks].map((e) => (
              <li key={e.id} className="[overflow-wrap:anywhere]">
                {e.title}
                {blockOf(e.day_block_id) ? <span className="text-ink-soft"> in {blockOf(e.day_block_id)!.title}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p>Nog niets</p>
        )}
      </section>
      <a
        href={`/dag/${date}`}
        onClick={(e) => {
          e.preventDefault();
          navigate(`/dag/${date}`);
        }}
        className="min-h-11 font-semibold text-pine"
      >
        Hele dag bekijken
      </a>
    </div>
  );
}

/** Blocks of a date that a routine fits in (its kinds in order of preference), for "Morgen" from the af en toe list. */
async function fittingBlock(routine: Routine, date: string): Promise<string | null> {
  const plan = await db.day_plans
    .where("plan_date")
    .equals(date)
    .filter((p) => !p.deleted_at)
    .first();
  if (!plan) return null;
  const blocks = sortBlocks(
    await db.day_blocks
      .where("day_plan_id")
      .equals(plan.id)
      .filter((b) => !b.deleted_at && !b.group_title && b.slot_kind !== "work")
      .toArray(),
  );
  for (const kind of routine.slot_kinds) {
    const block = blocks.find((b) => b.slot_kind === kind);
    if (block) return block.id;
  }
  return null;
}

/** The gym backup at 10:00, or the next whole hour when that has passed already (Snel plannen late in the morning). */
function defaultGymTime(date: string, today: string): string {
  if (date !== today) return "10:00";
  const hour = new Date().getHours();
  return hour < 10 ? "10:00" : `${String(Math.min(hour + 1, 21)).padStart(2, "0")}:00`;
}

/**
 * Stap 3: hooguit 3 inboxitems en hooguit 3 af en toe items die aan de beurt zijn, vast bij het openen van de stap
 * (wat je afhandelt, wordt niet aangevuld: de stap blijft licht), en de gym backup vraag.
 */
function PlanIn({ date, today }: { date: string; today: string }) {
  const inbox = useLiveQuery(async () =>
    (await db.tasks.where("status").equals("inbox").toArray())
      .filter((t) => !t.deleted_at)
      .sort((a, b) => (a.inbox_at < b.inbox_at ? -1 : 1)),
  );
  const routines = useRoutines();
  const completions = useCompletions();
  const plannedThere = useLiveQuery(
    async () =>
      new Set(
        (
          await db.plan_entries
            .where("plan_date")
            .equals(date)
            .filter((e) => e.kind === "routine" && !e.deleted_at)
            .toArray()
        ).map((e) => e.routine_id),
      ),
    [date],
  );
  if (!inbox || !routines || !completions || !plannedThere) return null;
  const taskIds = inbox.slice(0, 3).map((t) => t.id);
  const dueIds = occasionalList(routines, completions, today)
    .filter((i) => isDue(i) && !plannedThere.has(i.routine.id))
    .slice(0, 3)
    .map((i) => i.routine.id);
  return <PlanInList date={date} today={today} taskIds={taskIds} dueIds={dueIds} />;
}

function PlanInList({ date, today, taskIds, dueIds }: { date: string; today: string; taskIds: string[]; dueIds: string[] }) {
  const [shownTasks] = useState(taskIds);
  const [shownDue] = useState(dueIds);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [otherDay, setOtherDay] = useState<{ id: string; date: string } | null>(null);
  const [gymTime, setGymTime] = useState(() => defaultGymTime(date, today));
  const [gymAnswered, setGymAnswered] = useState(false);
  const inbox = useLiveQuery(async () =>
    (await db.tasks.where("status").equals("inbox").toArray())
      .filter((t) => !t.deleted_at)
      .sort((a, b) => (a.inbox_at < b.inbox_at ? -1 : 1)),
  );
  const routines = useRoutines();
  const completions = useCompletions();
  const rhythm = useRhythm();
  const weekEntries = useLiveQuery(
    () =>
      db.plan_entries
        .where("plan_date")
        .between(addDays(date, -7), addDays(date, 7), true, true)
        .filter((e) => e.kind === "routine")
        .toArray(),
    [date],
  );
  if (!inbox || !routines || !completions || !weekEntries) return null;

  const tasks = shownTasks.map((id) => inbox.find((t) => t.id === id)).filter((t): t is NonNullable<typeof t> => !!t && !skipped.has(t.id));
  const plannedThere = new Set(weekEntries.filter((e) => e.plan_date === date && !e.deleted_at).map((e) => e.routine_id));
  const due = shownDue
    .map((id) => routines.find((r) => r.id === id))
    .filter((r): r is Routine => !!r && !skipped.has(r.id) && !plannedThere.has(r.id))
    .map((routine) => ({ routine }));
  const gym = routines.find((r) => r.weekly_target);
  const offer = gym && !gymAnswered ? gymOffer(gym, date, completions, weekEntries, isPausedIn(rhythm)) : null;
  const skip = (id: string) => setSkipped((s) => new Set(s).add(id));

  return (
    <div className="flex flex-col gap-6">
      <AiPlanSuggestions date={date} />
      {offer && gym && (
        <section aria-labelledby="gym-backup" className="rounded-2xl border-2 border-pine/50 bg-surface p-3">
          <h2 id="gym-backup" className="font-semibold">
            {`${displayDate(date)
              .split(" ")[0]!
              .replace(/^./, (c) => c.toUpperCase())} gym inplannen?`}
          </h2>
          <p className="mb-2 text-ink-soft">
            {offer.needed === 1 ? "Deze week is nog 1 keer nodig." : `Deze week zijn nog ${offer.needed} keer nodig.`}
          </p>
          <label className="mb-2 flex items-center gap-3">
            <span>Om</span>
            <input
              type="time"
              value={gymTime}
              onChange={(e) => setGymTime(e.target.value || "10:00")}
              className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={async () => {
                const plan = await db.day_plans
                  .where("plan_date")
                  .equals(date)
                  .filter((p) => !p.deleted_at)
                  .first();
                const free = plan
                  ? await db.day_blocks
                      .where("day_plan_id")
                      .equals(plan.id)
                      .filter((b) => !b.deleted_at && b.slot_kind === "free")
                      .first()
                  : undefined;
                await planRoutineOn(gym, date, free?.id ?? null, gymTime);
                setGymAnswered(true);
                toast(`Gym ingepland om ${displayTime(gymTime)}`);
              }}
            >
              Ja, inplannen
            </Button>
            <Button variant="secondary" onClick={() => setGymAnswered(true)}>
              Nee
            </Button>
          </div>
        </section>
      )}

      <section aria-labelledby="uit-inbox">
        <h2 id="uit-inbox" className="mb-2 font-semibold">
          Nieuw
        </h2>
        {tasks.length ? (
          <ul className="flex flex-col gap-3">
            {tasks.map((t) => (
              <li key={t.id} className="rounded-2xl border border-mist bg-surface p-3">
                <p className="mb-2 [overflow-wrap:anywhere]">{t.title}</p>
                <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
                  <ActionButton label="Inplannen" title={t.title} onClick={() => openTask(t.id, { plan: true })} />
                  <ActionButton
                    label="Ooit"
                    title={t.title}
                    onClick={() => void setTaskStatus(t.id, "someday").then(() => toast("Naar Ooit"))}
                  />
                  <ActionButton
                    label="Weg"
                    title={t.title}
                    onClick={() => void setTaskStatus(t.id, "cancelled").then(() => toast("Weggezet"))}
                  />
                  <ActionButton label="Overslaan" title={t.title} onClick={() => skip(t.id)} />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Niets nieuws.</Empty>
        )}
      </section>

      <section aria-labelledby="af-en-toe">
        <h2 id="af-en-toe" className="mb-2 font-semibold">
          Af en toe, aan de beurt
        </h2>
        {due.length ? (
          <ul className="flex flex-col gap-3">
            {due.map(({ routine }) => (
              <li key={routine.id} className="rounded-2xl border border-mist bg-surface p-3">
                <p className="mb-2 [overflow-wrap:anywhere]">{routine.title}</p>
                <div className="grid grid-cols-3 gap-1">
                  <ActionButton
                    label={date === today ? "Vandaag" : "Morgen"}
                    title={routine.title}
                    onClick={async () => {
                      await planRoutineOn(routine, date, await fittingBlock(routine, date), null);
                      toast(`${routine.title} ingepland`);
                    }}
                  />
                  <ActionButton
                    label="Andere dag"
                    title={routine.title}
                    onClick={() => setOtherDay({ id: routine.id, date: addDays(date, 1) })}
                  />
                  <ActionButton label="Overslaan" title={routine.title} onClick={() => skip(routine.id)} />
                </div>
                {otherDay?.id === routine.id && (
                  <div className="mt-2 flex items-end gap-2">
                    <label className="flex flex-1 flex-col gap-1">
                      <span className="text-sm text-ink-soft">Op welke dag</span>
                      <input
                        type="date"
                        min={today}
                        value={otherDay.date}
                        onChange={(e) => setOtherDay({ id: routine.id, date: e.target.value || otherDay.date })}
                        className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3"
                      />
                    </label>
                    <Button
                      onClick={async () => {
                        await planRoutineOn(routine, otherDay.date, await fittingBlock(routine, otherDay.date), null);
                        toast(`${routine.title} ingepland op ${displayDate(otherDay.date)}`);
                        setOtherDay(null);
                        skip(routine.id);
                      }}
                    >
                      Inplannen
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Niets aan de beurt.</Empty>
        )}
      </section>
    </div>
  );
}

function ActionButton({ label, title, onClick }: { label: string; title: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${title}: ${label}`}
      className="min-h-11 rounded-lg border-2 border-mist px-1 text-sm font-semibold"
    >
      {label}
    </button>
  );
}

/** Stap 4: klaarleggen voor morgen (AV2); dezelfde lijst als in het blok Klaarleggen. */
export function LayOut({ date, onAllChecked }: { date: string; onAllChecked?: () => void }) {
  const rows = useChecklist(date);
  if (!rows) return null;
  if (!rows.length) return <Empty>Niets klaar te leggen. Pas de lijst aan in Meer, Klaarleggen.</Empty>;
  return (
    <ul aria-label="Klaarleggen" className="flex flex-col gap-1">
      {rows.map((row) => {
        const done = !!row.checkId;
        return (
          <li key={row.item.id}>
            <button
              type="button"
              role="checkbox"
              aria-checked={done}
              onClick={() => {
                void setChecked(row, date, !done);
                if (!done && rows.every((other) => other.item.id === row.item.id || other.checkId)) onAllChecked?.();
              }}
              className="flex min-h-12 w-full items-center gap-3 rounded-xl bg-surface px-3 text-left"
            >
              <span
                aria-hidden
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-md border-2",
                  done ? "border-pine bg-pine text-paper" : "border-ink-soft",
                )}
              >
                {done && <Check className="size-4" strokeWidth={3} />}
              </span>
              <span className={cn("min-w-0 flex-1 [overflow-wrap:anywhere]", done && "text-done line-through decoration-2")}>
                {row.item.title}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Stap 5: klaar. Geen confetti; wel wat er vandaag gedaan is. */
function Done({ today, forDate, quick }: { today: string; forDate: string; quick: boolean }) {
  const done = useLiveQuery(async () => {
    const plan = await db.day_plans
      .where("plan_date")
      .equals(today)
      .filter((p) => !p.deleted_at)
      .first();
    const blocks = plan
      ? await db.day_blocks
          .where("day_plan_id")
          .equals(plan.id)
          .filter((b) => !b.deleted_at && b.status === "done")
          .count()
      : 0;
    const entries = await db.plan_entries
      .where("plan_date")
      .equals(today)
      .filter((e) => !e.deleted_at && e.status === "done")
      .count();
    return blocks + entries;
  }, [today]);
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xl font-semibold">{quick ? "Vandaag staat klaar." : "Morgen staat klaar."}</p>
      {done !== undefined && !quick && (
        <p className="text-ink-soft">{done === 1 ? "Vandaag 1 ding gedaan." : `Vandaag ${done} dingen gedaan.`}</p>
      )}
      <Button onClick={() => navigate(quick ? "/" : `/dag/${forDate}`)}>{quick ? "Naar vandaag" : "Morgen bekijken"}</Button>
      <Button variant="secondary" onClick={() => navigate("/")}>
        Sluiten
      </Button>
    </div>
  );
}

/** De avondplanning stelt voor (AI golf B): a few things that fit, each with Ja or Nee. Nothing happens without Ja. */
function AiPlanSuggestions({ date }: { date: string }) {
  const ai = useAiEnabled();
  const routines = useRoutines();
  const [state, setState] = useState<"idle" | "busy" | PlanSuggestion[]>("idle");
  if (!ai) return null;
  if (state === "idle" || state === "busy")
    return (
      <Button
        variant="secondary"
        disabled={state === "busy"}
        onClick={async () => {
          setState("busy");
          try {
            const { suggestions } = await aiPlan(date);
            setState(suggestions);
            if (!suggestions.length) toast("Niets extra nodig voor morgen.");
          } catch (error) {
            setState("idle");
            toast(aiError(error, "Voorstellen"));
          }
        }}
      >
        <Sparkles aria-hidden className="size-5" />
        {state === "busy" ? "Bezig met kijken" : "Stel iets voor"}
      </Button>
    );
  if (!state.length) {
    // Nee on everything: the button is there again for another try.
    queueMicrotask(() => setState("idle"));
    return null;
  }
  const drop = (id: string) => setState((list) => (Array.isArray(list) ? list.filter((s) => s.id !== id) : list));
  return (
    <section aria-labelledby="ai-voorstel" className="rounded-2xl border-2 border-sun bg-surface p-3">
      <h2 id="ai-voorstel" className="mb-2 flex items-center gap-2 font-semibold">
        <Sparkles aria-hidden className="size-5 text-pine" />
        Voorstel voor morgen
      </h2>
      <ul className="flex flex-col gap-3">
        {state.map((s) => (
          <li key={s.id} className="rounded-xl bg-paper p-3">
            <p className="font-semibold [overflow-wrap:anywhere]">{s.title}</p>
            <p className="text-sm text-ink-soft">{s.reason}</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Button
                onClick={async () => {
                  if (s.kind === "task") await planTask(s.id, { date, dayBlockId: s.blockId });
                  else {
                    const routine = routines?.find((r) => r.id === s.id);
                    if (routine) await planRoutineOn(routine, date, s.blockId, null);
                  }
                  toast(`${s.title} ingepland`);
                  drop(s.id);
                }}
              >
                Ja
              </Button>
              <Button variant="secondary" onClick={() => drop(s.id)}>
                Nee
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Stap 2: when Tsjits has something tomorrow (B42), if the switch in Instellingen is on. */
function PartnerTomorrow({ date }: { date: string }) {
  const show = useShowPartner();
  const others = useOthersTime(date, date);
  const household = useHousehold();
  if (!show || !others?.length) return null;
  const nameOf = (id: string) => household?.find((h) => h.id === id)?.name ?? "De ander";
  return (
    <section className="rounded-2xl border border-mist bg-surface p-3">
      <h2 className="mb-1 text-sm text-ink-soft">{others.length ? `${nameOf(others[0]!.owner_id)} morgen` : "Samen"}</h2>
      <ul>
        {others.map((a) => (
          <li key={a.id} className="[overflow-wrap:anywhere]">
            {availabilityLine(a, nameOf(a.owner_id))}
          </li>
        ))}
      </ul>
    </section>
  );
}
